<template>
  <tr
    class="hover:bg-gray-50 transition-colors duration-150 ease-in-out cursor-pointer"
    @click="toggleTable"
  >
    <td class="p-4">
      <div class="flex items-center gap-2 min-w-0">
        <button
          type="button"
          class="btn-icon btn-icon-default flex-shrink-0"
          data-test="toggle-package-claims"
          @click.stop="toggleTable"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-transform duration-200"
            :class="{ 'rotate-90': open }">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <div class="flex items-center space-x-1 flex-shrink-0">
          <RouterLink
            :to="{ name: 'PackagesDashboard', params: { projectId: $route.params.projectId, packageId: subcontractorPackage.id } }"
            class="btn-icon btn-icon-blue block"
            title="View Package"
            @click.stop=""
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>
          </RouterLink>
        </div>

        <div class="min-w-0 text-sm">
          <p
            v-if="subcontractorPackage.subcontractor?.name"
            class="text-xs text-gray-500 text-wrap"
          >
            {{ subcontractorPackage.subcontractor.name }}
          </p>

          <p class="font-medium text-gray-900 text-wrap">
            {{ subcontractorPackage.name }}
          </p>

          <div class="flex items-center gap-2 text-xs text-gray-500">
            {{ australianCurrency(subcontractorPackage.totalAwarded) }}

            <span
              v-if="subcontractorPackage.procurement_method"
              class="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 capitalize"
            >
              {{ subcontractorPackage.procurement_method.replace(/_/g, ' ') }}
            </span>
          </div>
        </div>
      </div>
    </td>

    <td class="p-4 text-right text-sm">
      <template v-if="scopeGap">
        <p class="text-gray-900">
          {{ australianCurrency(scopeGapTotalForecast) }}
        </p>

        <p class="text-xs text-gray-500">
          {{ australianCurrency(scopeGapTotalFinalAmount) }}
        </p>
      </template>
    </td>

    <td class="p-4 text-right text-sm text-gray-900">
      {{ australianCurrency(approvedVariation) }}
    </td>

    <td class="p-4 text-right text-sm text-gray-900">
      {{ australianCurrency(finalAccount) }}
    </td>

    <td class="p-4 text-right text-sm text-gray-900">
      {{ australianCurrency(latestClaim?.totalActualForecast ?? 0) }}
    </td>

    <td class="p-4 text-right text-sm text-gray-900">
      {{ australianCurrency(finalForecast) }}
    </td>

    <td
      class="p-4 text-right"
      @click.stop=""
    >
      <button
        v-if="projectsStore.project?.unarchived"
        type="button"
        class="btn btn--primary-blue btn--small w-full"
        @click="createClaimForPackage(subcontractorPackage, latestClaim)"
      >
        <span>
          Create Claim
        </span>
      </button>
    </td>
  </tr>

  <tr
    v-if="open"
    class="bg-gray-50"
  >
    <td
      colspan="7"
      class="p-0 border-t border-gray-100"
    >
      <div
        v-if="fetchClaimsAction.is(States.LOADING)"
        class="p-4 text-center"
      >
        <AppSpinner class="h-6 w-6 mx-auto" />
      </div>

      <template v-else-if="fetchClaimsAction.is(States.COMPLETE)">
        <p
          v-if="!packageClaims.length"
          class="p-4 text-center text-sm text-gray-500"
        >
          No claims found for this package.
        </p>

        <table
          v-else
          class="min-w-full divide-y divide-gray-200"
        >
          <thead class="bg-gray-100">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Claim
              </th>

              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Period
              </th>

              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Time Bar
              </th>

              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Amount
              </th>

              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Cumulative Amount
              </th>

              <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" />
            </tr>
          </thead>

          <tbody class="bg-white divide-y divide-gray-100">
            <PackageRow
              v-for="claim in packageClaims"
              :key="claim.id"
              :claim="claim"
              :selected-month="DateTime.fromISO(claim.start).startOf('month')"
              :subcontractor-package="subcontractorPackage"
              :project-supplier="projectSupplier"
              :lite-mode="liteMode"
              @issue-claim="emits('issueClaim', $event)"
            />
          </tbody>
        </table>
      </template>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency } from '@/helpers';
import Claim, { ClaimStatus } from '@/models/Claim';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import { useClaimsStore } from '@/store/claims';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import PackageRow from './PackageRow.vue';

const props = withDefaults(
  defineProps<{
    subcontractorPackage: Package;
    projectSupplier: ProjectSupplier;
    projectId: string;
    liteMode?: boolean;
  }>(),
  {
    liteMode: false,
  },
);

const emits = defineEmits<{
  (e: 'createClaim', data: { projectSupplier: ProjectSupplier; packageData: Package; latestClaim?: Claim }): void;
  (e: 'issueClaim', data: { claim: Claim; email: string }): void;
}>();

const open = ref(false);
const claimsStore = useClaimsStore();
const packagesStore = usePackagesStore();
const projectsStore = useProjectsStore();
const fetchClaimsAction = useStoreApiAction(packagesStore.fetchSubcontractorDashboardShow);

const packageClaims = computed(() => {
  return claimsStore.models
    .whereHas('package_claim', (query) => {
      query.where('package_id', props.subcontractorPackage.id)
        .where('project_supplier_id', props.projectSupplier.id);
    })
    .with('scope_gap')
    .orderBy('claim_number', 'desc')
    .get();
});

const latestClaim = computed(() => {
  return packageClaims.value[0];
});

const scopeGap = computed(() => latestClaim.value?.scope_gap);
const scopeGapTotalForecast = computed(() => scopeGap.value?.totalForecast ?? 0);
const scopeGapTotalFinalAmount = computed(() => scopeGap.value?.totalFinalAmount ?? 0);
const approvedVariation = computed(() => props.subcontractorPackage.totalApprovedVariation);

const finalAccount = computed(() => {
  return props.subcontractorPackage.totalAwarded + approvedVariation.value;
});

const finalForecast = computed(() => {
  return scopeGapTotalFinalAmount.value +
    (latestClaim.value?.totalActualForecast ?? 0);
});

const fetchClaims = () => {
  fetchClaimsAction.request(props.projectId, props.subcontractorPackage.id);
};

const toggleTable = () => {
  open.value = !open.value;

  if(open.value) {
    fetchClaims();
  }
};

const createClaimForPackage = (packageData: Package, latestClaim?: Claim) => {
  emits('createClaim', { projectSupplier: props.projectSupplier, packageData, latestClaim });
};
</script>

<style scoped></style>
